import React from 'react'

interface AngelWingsProps {
  size?: number
  className?: string
  animated?: boolean
  color?: 'white' | 'gold' | 'blue' | 'pink'
}

const AngelWings: React.FC<AngelWingsProps> = ({ 
  size = 100, 
  className = '', 
  animated = true,
  color = 'white'
}) => {
  const getWingColors = () => {
    switch (color) {
      case 'gold':
        return { primary: '#ffd700', secondary: '#fff8dc', accent: '#ffed4e' }
      case 'blue':
        return { primary: '#3b82f6', secondary: '#dbeafe', accent: '#60a5fa' }
      case 'pink':
        return { primary: '#ec4899', secondary: '#fce7f3', accent: '#f9a8d4' }
      default:
        return { primary: '#ffffff', secondary: '#f8fafc', accent: '#e2e8f0' }
    }
  }

  const colors = getWingColors()

  return (
    <div className={className} style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id={`wingGradient-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.primary} />
            <stop offset="50%" stopColor={colors.secondary} />
            <stop offset="100%" stopColor={colors.accent} />
          </linearGradient>
          
          <linearGradient id={`featherGradient-${color}`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={colors.secondary} />
            <stop offset="100%" stopColor={colors.primary} />
          </linearGradient>

          <filter id="wingGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge> 
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>

          <pattern id={`featherPattern-${color}`} patternUnits="userSpaceOnUse" width="4" height="8">
            <path d="M 2 0 Q 1 4 2 8" stroke={colors.accent} strokeWidth="0.5" fill="none" opacity="0.3" />
          </pattern>
        </defs>

        {/* Left Wing */}
        <g className="left-wing">
          {/* Main Wing Shape */}
          <path 
            d="M 50 50 Q 20 30 15 45 Q 10 60 25 65 Q 40 60 50 50" 
            fill={`url(#wingGradient-${color})`}
            filter="url(#wingGlow)"
          />
          
          {/* Wing Layers */}
          <path 
            d="M 50 50 Q 25 35 22 45 Q 18 55 30 60 Q 42 55 50 50" 
            fill={colors.secondary} 
            opacity="0.8"
          />
          
          <path 
            d="M 50 50 Q 30 40 28 47 Q 25 53 35 56 Q 44 53 50 50" 
            fill={colors.primary} 
            opacity="0.6"
          />

          {/* Feather Details */}
          <g fill={`url(#featherPattern-${color})`}>
            <ellipse cx="25" cy="45" rx="8" ry="3" opacity="0.4" />
            <ellipse cx="30" cy="50" rx="6" ry="2" opacity="0.3" />
            <ellipse cx="35" cy="52" rx="4" ry="1.5" opacity="0.3" />
          </g>

          {/* Individual Feathers */}
          <g opacity="0.6">
            <path d="M 20 40 Q 18 42 20 44" stroke={colors.accent} strokeWidth="1" fill="none" />
            <path d="M 15 50 Q 13 52 15 54" stroke={colors.accent} strokeWidth="1" fill="none" />
            <path d="M 25 58 Q 23 60 25 62" stroke={colors.accent} strokeWidth="1" fill="none" />
            <path d="M 35 55 Q 33 57 35 59" stroke={colors.accent} strokeWidth="1" fill="none" />
          </g>

          {/* Wing Animation */}
          {animated && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 50 50;-10 50 50;0 50 50"
              dur="3s"
              repeatCount="indefinite"
            />
          )}
        </g>

        {/* Right Wing */}
        <g className="right-wing">
          {/* Main Wing Shape */}
          <path 
            d="M 50 50 Q 80 30 85 45 Q 90 60 75 65 Q 60 60 50 50" 
            fill={`url(#wingGradient-${color})`}
            filter="url(#wingGlow)"
          />
          
          {/* Wing Layers */}
          <path 
            d="M 50 50 Q 75 35 78 45 Q 82 55 70 60 Q 58 55 50 50" 
            fill={colors.secondary} 
            opacity="0.8"
          />
          
          <path 
            d="M 50 50 Q 70 40 72 47 Q 75 53 65 56 Q 56 53 50 50" 
            fill={colors.primary} 
            opacity="0.6"
          />

          {/* Feather Details */}
          <g fill={`url(#featherPattern-${color})`}>
            <ellipse cx="75" cy="45" rx="8" ry="3" opacity="0.4" />
            <ellipse cx="70" cy="50" rx="6" ry="2" opacity="0.3" />
            <ellipse cx="65" cy="52" rx="4" ry="1.5" opacity="0.3" />
          </g>

          {/* Individual Feathers */}
          <g opacity="0.6">
            <path d="M 80 40 Q 82 42 80 44" stroke={colors.accent} strokeWidth="1" fill="none" />
            <path d="M 85 50 Q 87 52 85 54" stroke={colors.accent} strokeWidth="1" fill="none" />
            <path d="M 75 58 Q 77 60 75 62" stroke={colors.accent} strokeWidth="1" fill="none" />
            <path d="M 65 55 Q 67 57 65 59" stroke={colors.accent} strokeWidth="1" fill="none" />
          </g>

          {/* Wing Animation */}
          {animated && (
            <animateTransform
              attributeName="transform"
              type="rotate"
              values="0 50 50;10 50 50;0 50 50"
              dur="3s"
              repeatCount="indefinite"
            />
          )}
        </g>

        {/* Central Halo */}
        <circle 
          cx="50" 
          cy="25" 
          r="8" 
          fill="none" 
          stroke={colors.primary} 
          strokeWidth="2" 
          opacity="0.8"
          filter="url(#wingGlow)"
        >
          {animated && (
            <animate attributeName="opacity" values="0.6;1;0.6" dur="4s" repeatCount="indefinite" />
          )}
        </circle>

        {/* Halo Sparkles */}
        {animated && (
          <g className="halo-sparkles">
            <circle cx="42" cy="20" r="1" fill={colors.primary}>
              <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="0s" />
            </circle>
            <circle cx="58" cy="20" r="1" fill={colors.primary}>
              <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="0.5s" />
            </circle>
            <circle cx="50" cy="15" r="1" fill={colors.primary}>
              <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="1s" />
            </circle>
            <circle cx="50" cy="35" r="1" fill={colors.primary}>
              <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite" begin="1.5s" />
            </circle>
          </g>
        )}

        {/* Floating Light Particles */}
        {animated && (
          <g className="light-particles">
            <circle cx="30" cy="30" r="0.8" fill={colors.accent} opacity="0.7">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0;10,-10;20,-20"
                dur="5s"
                repeatCount="indefinite"
              />
              <animate attributeName="opacity" values="0;0.8;0" dur="5s" repeatCount="indefinite" />
            </circle>
            
            <circle cx="70" cy="30" r="0.6" fill={colors.primary} opacity="0.6">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0;-10,-10;-20,-20"
                dur="6s"
                repeatCount="indefinite"
              />
              <animate attributeName="opacity" values="0;0.7;0" dur="6s" repeatCount="indefinite" />
            </circle>
            
            <circle cx="50" cy="70" r="1" fill={colors.secondary} opacity="0.5">
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0;0,-30;0,-60"
                dur="7s"
                repeatCount="indefinite"
              />
              <animate attributeName="opacity" values="0;0.6;0" dur="7s" repeatCount="indefinite" />
            </circle>
          </g>
        )}

        {/* Divine Light Rays */}
        {animated && (
          <g className="light-rays" opacity="0.3">
            <line x1="50" y1="25" x2="50" y2="5" stroke={colors.primary} strokeWidth="1">
              <animate attributeName="opacity" values="0;0.5;0" dur="3s" repeatCount="indefinite" />
            </line>
            <line x1="42" y1="27" x2="35" y2="12" stroke={colors.primary} strokeWidth="0.8">
              <animate attributeName="opacity" values="0;0.4;0" dur="3.5s" repeatCount="indefinite" />
            </line>
            <line x1="58" y1="27" x2="65" y2="12" stroke={colors.primary} strokeWidth="0.8">
              <animate attributeName="opacity" values="0;0.4;0" dur="2.8s" repeatCount="indefinite" />
            </line>
          </g>
        )}
      </svg>
    </div>
  )
}

export default AngelWings
